import { useLoadingBar } from '@/hooks/web/useLoadingBar';
import { useMessage } from '@/hooks/web/useMessage';
import { useDialog } from '@/hooks/web/useDialog';

import { ACCESS_TOKEN, CURRENT_USER } from '@/store/mutation-types';
import { PageEnum } from '@/enums/pageEnum';
import router from '@/router';

// Clear the session and send the user to the login page (a clean redirect, not a
// reload of the current deep route — deep routes 404 on hard reload).
function goToLogin() {
  localStorage.removeItem(ACCESS_TOKEN);
  localStorage.removeItem(CURRENT_USER);
  sessionStorage.removeItem(ACCESS_TOKEN);
  sessionStorage.removeItem(CURRENT_USER);
  window.location.href = PageEnum.BASE_LOGIN;
}

// Guards against stacking one "session expired" dialog per in-flight request.
let sessionExpiredShown = false;

export function checkStatus(status: number, msg: string): void {
  const $loadingBar: any = useLoadingBar();
  const $message: any = useMessage();
  const $dialog: any = useDialog();
  switch (status) {
    case 400:
      $message.error(msg);
      break;
    // 401: 未登录
    // 未登录则跳转登录页面，并携带当前页面的路径
    // 在登录成功后返回当前页面，这一步需要在登录页操作。
    case 401:
      // 401 = the session is no longer authenticated (e.g. the token expired).
      // Silently clear the session and go to the login page — no dialog. Skip when
      // already on the login page so genuine sign-in errors keep their own message.
      if (router.currentRoute.value?.name === PageEnum.BASE_LOGIN_NAME) break;
      if (sessionExpiredShown) break;
      sessionExpiredShown = true;
      $loadingBar.error();
      goToLogin();
      break;
    case 403:
      // A 403 here is (almost always) the Azure WAF (firewall) rejecting the request
      // body BEFORE it reaches the app — e.g. a field contains a URL ("://") or another
      // pattern the managed rules flag. It is NOT an auth failure — that is 401 — so we
      // must NOT clear the session or log the user out. Just surface the error and let
      // the user fix the offending field and retry. (The WAF returns a bare 403 with no
      // body, so msg is usually empty — provide a helpful default.)
      $dialog.error({
        title: '403 Forbidden',
        content:
          msg ||
          'This request was blocked by the server firewall. A field may contain a web link (URL) or restricted characters — please review your input and try again.',
        positiveText: 'Close',
        maskClosable: false,
        closable: true,
        onPositiveClick: () => {
          $loadingBar.error();
        },
      });
      break;
    // 404请求不存在
    case 404:
      // $message.error('网络请求错误，未找到该资源!');
      break;
    case 405:
      // $message.error('网络请求错误，请求方法未允许!');
      $message.error('405 Method Not Allowed');
      break;
    case 408:
      // $message.error('网络请求超时');
      $message.error('408 Request Timeout');
      break;
    case 500:
      // $message.error('服务器错误,请联系管理员!');
      $dialog.error({
        title: 'Error',
        content: msg,
        positiveText: 'Close',
        maskClosable: false,
        closable: true,
        onPositiveClick: () => {
          $loadingBar.error();
        },
      });
      break;
    case 501:
      // $message.error('网络未实现');
      $message.error('501 Not Implemented');
      break;
    case 502:
      // $message.error('网络错误');
      $message.error('502 Bad Gateway');
      break;
    case 503:
      // $message.error('服务不可用，服务器暂时过载或维护!');
      $message.error('503 Service Unavailable');
      break;
    case 504:
      // $message.error('网络超时');
      $message.error('504 Gateway Timeout');
      break;
    case 505:
      // $message.error('http版本不支持该请求!');
      $message.error('505 HTTP Version Not Supported');
      break;
    default:
      // $message.error(msg);
      $dialog.error({
        title: 'Unknown Error',
        content: msg,
        positiveText: 'Close',
        maskClosable: false,
        closable: true,
        onPositiveClick: () => {
          $loadingBar.error();
        },
      });
  }
  $message.destroyAll();
}
